import { useStanQuery } from '.';
import { mainApi } from '@/lib/api';
import { QUERY_KEYS, StoreType } from '@/lib/constants';
import { Store, StoreResponse } from '@/types/store';

// API function to fetch store data
const _fetchStoreData = async (storeId: number, storeType = 'linksite'): Promise<Store[]> => {
    const response = await mainApi.get<StoreResponse>(`v1/stores/${storeId}`, {
        params: { type: storeType },
    });

    // Handle both single store response and stores array response
    const data = response.data;
    if ('store' in data) {
        return [data.store];
    } else {
        throw new Error('No store data found in response');
    }
};

const _fetchStoresByType = async (storeType = 'linksite'): Promise<Store[]> => {
    const response = await mainApi.get<{ stores: Store[] }>(`v1/stores/self`, {
        params: { type: storeType },
    });
    return response.data.stores || [];
};

export function useStoreData(storeId?: number | null) {
    return useStanQuery<Store[]>(
        {
            queryKey: [QUERY_KEYS.STORE_DATA, storeId, StoreType.LINKSITE],
            queryFn: async () => {
                if (!storeId) return [];
                return await _fetchStoreData(storeId, StoreType.LINKSITE);
            },
            staleTime: 5 * 60 * 1000, // 5 minutes
            gcTime: 10 * 60 * 1000, // 10 minutes
            retry: 2,
            meta: {
                error: {
                    sentryMessage: '[STORE_DATA] failed-to-fetch-store-data',
                },
            },
        },
        true // Skip page loading - StoreProvider is global, pages should handle their own loading states
    );
}

export function useMyStore() {
    return useStanQuery<Store[]>(
        {
            queryKey: [QUERY_KEYS.STORE_DATA, 'all', StoreType.LINKSITE],
            queryFn: async () => {
                return await _fetchStoresByType(StoreType.LINKSITE);
            },
            staleTime: 5 * 60 * 1000,
            gcTime: 10 * 60 * 1000,
            retry: 2,
            meta: {
                error: {
                    sentryMessage: '[MY_STORE] failed-to-fetch-store-data',
                },
            },
        },
        true
    );
}

export function useFunnelsData(enabled: boolean = true) {
    return useStanQuery<Store[]>(
        {
            queryKey: [QUERY_KEYS.STORE_DATA, StoreType.FUNNEL],
            queryFn: async () => {
                return await _fetchStoresByType(StoreType.FUNNEL);
            },
            staleTime: 5 * 60 * 1000,
            gcTime: 10 * 60 * 1000,
            retry: 2,
            enabled,
            meta: {
                error: {
                    sentryMessage: '[STORE_DATA] failed-to-fetch-funnels-data',
                },
            },
        },
        true // Skip page loading - StoreProvider is global, pages should handle their own loading states
    );
}
